import React, { useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { ArrowLeft, FileText, Save, Upload } from 'lucide-react'
import toast from 'react-hot-toast'

import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { useAuthStore } from '@/store/auth.store'
import { useSettingsStore } from '@/store/settings.store'
import { itemService } from '@/services/itemService'
import { itemCategoriesService } from '@/services/itemCategoriesService'
import { showSaveSuccess } from '@/lib/swal'
import type { ItemLookupOption, ItemUpsertPayload } from '@/api/types'

type FormState = Omit<ItemUpsertPayload, 'attachmentFile'> & { attachmentFile: File | null }

export const InventoryItemEdit: React.FC = () => {
  const { t } = useTranslation('common')
  const navigate = useNavigate()
  const { id } = useParams<{ id: string }>()
  const { user } = useAuthStore()
  const { language = 'en' } = useSettingsStore()
  const branchId = user?.selectedBranch?.branchId
  const userId = user?.userId || 0
  const isNew = !id || id === 'new'
  const numericId = Number(id || 0)
  const isEditMode = !isNew && Number.isFinite(numericId) && numericId > 0

  const [saving, setSaving] = useState(false)
  const [loading, setLoading] = useState(false)
  const [categories, setCategories] = useState<ItemLookupOption[]>([])
  const [subCategories, setSubCategories] = useState<ItemLookupOption[]>([])
  const [uomOptions, setUomOptions] = useState<ItemLookupOption[]>([])
  const [purchaseTaxes, setPurchaseTaxes] = useState<ItemLookupOption[]>([])
  const [salesTaxes, setSalesTaxes] = useState<ItemLookupOption[]>([])
  const [imagePreviewUrl, setImagePreviewUrl] = useState('')
  const [form, setForm] = useState<FormState>({
    itemId: 0,
    itemNameEn: '',
    itemNameAr: '',
    itemCode: '',
    reference: '',
    itemCategoryId: 0,
    sku: '',
    barcode: '',
    tags: '',
    shelfId: '',
    description: '',
    subCategoryId: 0,
    uom: 0,
    type: 0,
    costMethod: 0,
    isTaxable: false,
    tax: 0,
    sellingTax: 0,
    attachmentFile: null,
    createdDate: undefined,
    createdBy: undefined,
    modifiedDate: undefined,
    modifiedBy: undefined,
    branchId: branchId || 0,
  })

  useEffect(() => {
    setForm((prev) => ({ ...prev, branchId: branchId || 0 }))
  }, [branchId])

  useEffect(() => {
    if (!branchId) return
    const loadLookups = async () => {
      try {
        const [cats, uoms, purchaseTaxLookups, salesTaxLookups] = await Promise.all([
          itemService.getCategories(branchId),
          itemService.getMaterialUOMsLookups(),
          itemService.getPurchaseTaxes(branchId),
          itemService.getSalesTaxes(branchId),
        ])
        setCategories(cats)
        setSubCategories([])
        setUomOptions(uoms)
        setPurchaseTaxes(purchaseTaxLookups || [])
        setSalesTaxes(salesTaxLookups || [])
      } catch {
        setCategories([])
        setSubCategories([])
        setUomOptions([])
        setPurchaseTaxes([])
        setSalesTaxes([])
      }
    }
    void loadLookups()
  }, [branchId])

  useEffect(() => {
    const loadSubCategoriesByCategory = async () => {
      if (!form.itemCategoryId) {
        setSubCategories([])
        setForm((prev) => ({ ...prev, subCategoryId: 0 }))
        return
      }
      try {
        const subs = await itemCategoriesService.getSubCategoriesByCategoryId(form.itemCategoryId)
        const mapped: ItemLookupOption[] = subs.map((x) => ({
          id: x.id,
          nameEn: x.name,
          nameAr: x.name,
        }))
        setSubCategories(mapped)
        if (!mapped.some((x) => x.id === form.subCategoryId)) {
          setForm((prev) => ({ ...prev, subCategoryId: 0 }))
        }
      } catch {
        setSubCategories([])
        setForm((prev) => ({ ...prev, subCategoryId: 0 }))
      }
    }
    void loadSubCategoriesByCategory()
  }, [form.itemCategoryId, form.subCategoryId])

  useEffect(() => {
    if (!branchId || !isEditMode) return
    const load = async () => {
      try {
        setLoading(true)
        const data = await itemService.getItemById(numericId, branchId)
        setForm({
          itemId: data.itemId,
          itemNameEn: data.itemNameEn || '',
          itemNameAr: data.itemNameAr || '',
          itemCode: data.itemCode || '',
          reference: data.reference || '',
          itemCategoryId: data.itemCategoryId || 0,
          sku: data.sku || '',
          barcode: data.barcode || '',
          tags: data.tags || '',
          shelfId: data.shelfId || '',
          description: data.description || '',
          subCategoryId: data.subCategoryId || 0,
          uom: data.uom || 0,
          type: data.type || 0,
          costMethod: data.costMethod || 0,
          isTaxable: data.isTaxable,
          tax: data.tax || 0,
          sellingTax: data.sellingTax || 0,
          attachmentFile: null,
          createdDate: data.createdDate,
          createdBy: data.createdBy,
          modifiedDate: data.modifiedDate,
          modifiedBy: data.modifiedBy,
          branchId: data.branchId || branchId,
        })
        setImagePreviewUrl(data.attachmentUrl || '')
      } catch (error: any) {
        toast.error(error.response?.data?.message || t('inventoryItems.failedToLoadOne'))
      } finally {
        setLoading(false)
      }
    }
    void load()
  }, [branchId, isEditMode, numericId, t])

  const categoryOptions = useMemo(
    () =>
      categories.map((x) => ({
        value: String(x.id),
        label: language === 'ar' ? x.nameAr || x.nameEn : x.nameEn || x.nameAr,
      })),
    [categories, language]
  )

  const subCategoryOptions = useMemo(
    () =>
      subCategories.map((x) => ({
        value: String(x.id),
        label: language === 'ar' ? x.nameAr || x.nameEn : x.nameEn || x.nameAr,
      })),
    [subCategories, language]
  )

  const uomSelectOptions = useMemo(
    () =>
      uomOptions.map((x) => ({
        value: String(x.id),
        label: language === 'ar' ? x.nameAr || x.nameEn : x.nameEn || x.nameAr,
      })),
    [uomOptions, language]
  )

  const salesTaxOptions = useMemo(() => {
    return salesTaxes.map((x) => ({
      value: String(x.id),
      label: language === 'ar' ? x.nameAr || x.nameEn : x.nameEn || x.nameAr,
    }))
  }, [salesTaxes, language])

  const purchaseTaxOptions = useMemo(() => {
    return purchaseTaxes.map((x) => ({
      value: String(x.id),
      label: language === 'ar' ? x.nameAr || x.nameEn : x.nameEn || x.nameAr,
    }))
  }, [purchaseTaxes, language])

  const handleBack = () => navigate('/inventory/items')

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    console.log('[InventoryItemEdit] submit clicked', {
      isEditMode,
      branchId,
      itemNameEn: form.itemNameEn,
      itemNameAr: form.itemNameAr,
      itemCategoryId: form.itemCategoryId,
    })
    if (!branchId) {
      console.warn('[InventoryItemEdit] submit blocked: branch not selected')
      toast.error(t('common.selectBranchFirst'))
      return
    }
    if (!form.itemNameEn.trim()) {
      console.warn('[InventoryItemEdit] submit blocked: Name (English) is required')
      toast.error(t('inventoryItems.validationNameEn'))
      return
    }
    if (!form.itemNameAr.trim()) {
      console.warn('[InventoryItemEdit] submit blocked: Name (Arabic) is required')
      toast.error(t('inventoryItems.validationNameAr'))
      return
    }
    if (!form.itemCategoryId) {
      console.warn('[InventoryItemEdit] submit blocked: Category is required')
      toast.error(t('inventoryItems.validationCategory'))
      return
    }

    try {
      setSaving(true)
      const payload: ItemUpsertPayload = {
        ...form,
        itemId: isEditMode ? form.itemId : 0,
        branchId,
        createdDate: form.createdDate || new Date().toISOString(),
        createdBy: form.createdBy || userId,
        modifiedDate: new Date().toISOString(),
        modifiedBy: userId,
      }
      console.log('[InventoryItemEdit] save payload:', payload)

      if (isEditMode) {
        await itemService.update(payload)
      } else {
        await itemService.save(payload)
      }

      toast.dismiss()
      await showSaveSuccess(isEditMode)
      navigate('/inventory/items')
    } catch (error: any) {
      toast.error(error.response?.data?.message || t('inventoryItems.saveFailed'))
    } finally {
      setSaving(false)
    }
  }

  const onFileChange = (file: File | null) => {
    setForm((prev) => ({ ...prev, attachmentFile: file }))
    if (!file) return
    const url = URL.createObjectURL(file)
    setImagePreviewUrl(url)
  }

  return (
    <div className="space-y-6 p-4 sm:p-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div className="flex gap-3">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
            <FileText className="h-5 w-5" />
          </div>
          <div>
            <h1 className="text-2xl font-semibold tracking-tight">{t('inventoryItems.title')}</h1>
            <p className="text-muted-foreground text-sm">{t('inventoryItems.formSubtitle')}</p>
          </div>
        </div>
      </div>

      <Card>
        <CardHeader className="border-b pb-4">
          <CardTitle className="text-lg">{t('inventoryItems.sectionItem')}</CardTitle>
        </CardHeader>
        <CardContent className="pt-6">
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid gap-6 lg:grid-cols-4">
              <div className="space-y-3 lg:col-span-1">
                <Label>{t('inventoryItems.profileImage')}</Label>
                <div className="flex h-52 items-center justify-center rounded-lg border bg-muted/20">
                  {imagePreviewUrl ? (
                    <img src={imagePreviewUrl} alt="item profile" className="h-full w-full rounded-lg object-cover" />
                  ) : (
                    <span className="text-sm text-muted-foreground">{t('inventoryItems.noImage')}</span>
                  )}
                </div>
                <div>
                  <Input
                    type="file"
                    accept="image/*"
                    onChange={(e) => onFileChange(e.target.files?.[0] || null)}
                    disabled={saving || loading}
                  />
                  <p className="mt-1 text-xs text-muted-foreground">
                    <Upload className="mr-1 inline h-3.5 w-3.5" />
                    {t('inventoryItems.imageHint')}
                  </p>
                </div>
              </div>

              <div className="space-y-6 lg:col-span-3 lg:pt-8">
                <div className="grid gap-4 sm:grid-cols-2">
                  <div className="space-y-2">
                    <Label htmlFor="inv-name-en">{t('inventoryItems.nameEn')} *</Label>
                    <Input
                      id="inv-name-en"
                      value={form.itemNameEn}
                      onChange={(e) => setForm((p) => ({ ...p, itemNameEn: e.target.value }))}
                      disabled={saving || loading}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="inv-name-ar">{t('inventoryItems.nameAr')} *</Label>
                    <Input
                      id="inv-name-ar"
                      value={form.itemNameAr}
                      onChange={(e) => setForm((p) => ({ ...p, itemNameAr: e.target.value }))}
                      disabled={saving || loading}
                    />
                  </div>
                </div>

                {isEditMode ? (
                  <div className="grid gap-4 sm:grid-cols-2">
                    <div className="space-y-2">
                      <Label htmlFor="inv-code">{t('inventoryItems.itemCode')}</Label>
                      <Input id="inv-code" value={form.itemCode} readOnly disabled />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="inv-barcode">{t('inventoryItems.barcode')}</Label>
                      <Input id="inv-barcode" value={form.barcode} readOnly disabled />
                    </div>
                  </div>
                ) : null}
              </div>
            </div>

            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              <div className="space-y-2">
                <Label htmlFor="inv-reference">{t('inventoryItems.reference')}</Label>
                <Input
                  id="inv-reference"
                  value={form.reference}
                  onChange={(e) => setForm((p) => ({ ...p, reference: e.target.value }))}
                  disabled={saving || loading}
                />
              </div>
              <div className="space-y-2">
                <Label>{t('inventoryItems.category')} *</Label>
                <Select
                  value={String(form.itemCategoryId || 0)}
                  onValueChange={(v) => setForm((p) => ({ ...p, itemCategoryId: Number(v || 0) }))}
                  disabled={saving || loading}
                >
                  <SelectTrigger>
                    <SelectValue placeholder={t('inventoryItems.selectCategory')} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="0">{t('inventoryItems.selectCategory')}</SelectItem>
                    {categoryOptions.map((x) => (
                      <SelectItem key={x.value} value={x.value}>
                        {x.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>{t('inventoryItems.subCategory')}</Label>
                <Select
                  value={String(form.subCategoryId || 0)}
                  onValueChange={(v) => setForm((p) => ({ ...p, subCategoryId: Number(v || 0) }))}
                  disabled={saving || loading}
                >
                  <SelectTrigger>
                    <SelectValue placeholder={t('inventoryItems.selectSubCategory')} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="0">{t('inventoryItems.selectSubCategory')}</SelectItem>
                    {subCategoryOptions.map((x) => (
                      <SelectItem key={x.value} value={x.value}>
                        {x.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              <div className="space-y-2">
                <Label>{t('inventoryItems.uom')}</Label>
                <Select
                  value={String(form.uom || 0)}
                  onValueChange={(v) => setForm((p) => ({ ...p, uom: Number(v || 0) }))}
                  disabled={saving || loading}
                >
                  <SelectTrigger>
                    <SelectValue placeholder={t('inventoryItems.selectUom')} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="0">{t('inventoryItems.selectUom')}</SelectItem>
                    {uomSelectOptions.map((x) => (
                      <SelectItem key={x.value} value={x.value}>
                        {x.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>{t('inventoryItems.purchaseTax')}</Label>
                <Select
                  value={String(form.tax ?? 0)}
                  onValueChange={(v) => setForm((p) => ({ ...p, tax: Number(v || 0) }))}
                  disabled={saving || loading}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="—" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="0">—</SelectItem>
                      {purchaseTaxOptions.map((x) => (
                      <SelectItem key={x.value} value={x.value}>
                        {x.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="inv-sales-tax">{t('inventoryItems.salesTax')}</Label>
                <Select
                  value={String(form.sellingTax ?? 0)}
                  onValueChange={(v) => setForm((p) => ({ ...p, sellingTax: Number(v || 0) }))}
                  disabled={saving || loading}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="—" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="0">—</SelectItem>
                    {salesTaxOptions.map((x) => (
                      <SelectItem key={x.value} value={x.value}>
                        {x.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="inv-desc">{t('inventoryItems.description')}</Label>
                <Input
                  id="inv-desc"
                  value={form.description}
                  onChange={(e) => setForm((p) => ({ ...p, description: e.target.value }))}
                  disabled={saving || loading}
                />
              </div>
            </div>

            <div className="flex justify-end gap-2 border-t pt-4">
              <Button type="button" variant="outline" onClick={handleBack} disabled={saving || loading}>
                <ArrowLeft className="mr-2 h-4 w-4" />
                {t('common.back')}
              </Button>
              <Button type="submit" disabled={saving || loading}>
                <Save className="mr-2 h-4 w-4" />
                {t('inventoryItems.save')}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  )
}
